import dayjs from "dayjs";
import { fmtQty } from "../executionNorm";

// Consumed cost heads of a machine on the web Mechanical sheet — the pure part
// of the civil-date view, kept out of the component so it can be tested.
//
// The civil date owns its booking (user, 2026-10-26): whichever day's log
// entered a head on a date, the drawer shows ONE qty and ONE rate for it, and
// what the clerk types replaces the date's booking (the server takes it over
// from the other logs). So a date's boxes start from the whole booking:
// every log's share folded into one qty, priced at the blended rate.

export interface HeadDraft {
  qty: string;
  uomId: number;
  rate: string;
}

export interface MergedHead {
  qty: string;
  /** Σ amount ÷ Σ qty across the logs, 2 dp; '' when none carried money. */
  rate: string;
  /** 'DD-MMM log (x of y h)' per OTHER log that booked here, for the hint. */
  from: string;
}

interface EarlierHeadRow {
  head: string;
  qty: string;
  rate: string;
  amount: string;
  logDate: string;
  hoursHere: string;
  logHours: string;
}

interface TodayHeadRow {
  head: string;
  qty: string;
  rate: string;
  amount: string;
}

// Same test as daySheet.isPresent, kept local so this module stays free of the
// API client (jest cannot load axios).
const isPresent = (value: string): boolean => value !== "" && Number.isFinite(Number(value));
const q4 = (v: number) => Math.round(v * 10000) / 10000;
const money = (v: number) => Math.round(v * 100) / 100;
const amountOf = (row: { qty: string; rate: string; amount: string }): number => {
  if (isPresent(row.amount)) return Number(row.amount);
  return isPresent(row.qty) && isPresent(row.rate) ? Number(row.qty) * Number(row.rate) : 0;
};

/** The date's whole booking of one head across every log, or null when none booked it. */
export const mergedHeadOf = (
  day: { earlierHeads?: EarlierHeadRow[]; todayHeads?: TodayHeadRow[] } | undefined,
  key: string
): MergedHead | null => {
  const earlier = (day?.earlierHeads ?? []).filter((x) => x.head === key);
  const today = (day?.todayHeads ?? []).filter((x) => x.head === key);
  const rows = [...earlier, ...today];
  if (!rows.length) return null;
  const qty = q4(rows.reduce((sum, x) => sum + (isPresent(x.qty) ? Number(x.qty) : 0), 0));
  const amount = money(rows.reduce((sum, x) => sum + amountOf(x), 0));
  return {
    qty: qty > 0 ? String(qty) : "",
    rate: qty > 0 && amount > 0 ? String(money(amount / qty)) : "",
    from: earlier
      .map((x) =>
        isPresent(x.hoursHere) && isPresent(x.logHours)
          ? `${dayjs(x.logDate).format("DD-MMM")} log (${fmtQty(x.hoursHere)} of ${fmtQty(x.logHours)} h)`
          : `${dayjs(x.logDate).format("DD-MMM")} log`
      )
      .join(", "),
  };
};

/** qty × rate, null unless both are present. */
export const todayHeadAmount = (h: HeadDraft): number | null =>
  isPresent(h.qty) && isPresent(h.rate) ? money(Number(h.qty) * Number(h.rate)) : null;
